import { useState, forwardRef, ReactNode } from 'react'
import styled from '@emotion/styled'
import { rem } from 'polished'
import debounce from 'lodash.debounce'
import { fontSizes, space, BOXSHADOWS } from 'utils'
import { Flex, IFlexProps } from 'components/atoms/Layout'
import { ITextProps, Text } from 'components/atoms/Typography'
import { Popover } from 'components'
import theme from '@nextretreat/ui-components/dist/Theme'

interface IInfoProps {
  fill?: string
  iconSize?: number
}

export interface ITooltipProps {
  text?: string
  label?: string
  onClick?: () => void
  children?: ReactNode
  iconSize?: number
  iconFill?: string
  position?: string
  maxWidth?: string
  contentComp?: ReactNode
  textAlign?: ITextProps['textAlign']
  justifyContent?: string
  width?: string
  fontSize?: string
}

interface IContentProps extends IFlexProps {
  hasText?: boolean
}

const StyledFlex = styled(Flex)`
  cursor: pointer;
  width: 100%;
`

const TooltipRef = forwardRef((props: IFlexProps, ref) => (
  //@ts-ignore
  <StyledFlex {...props} ref={ref} />
))

const Info = ({ fill, iconSize = 16 }: IInfoProps) => (
  <svg
    width={iconSize}
    height={iconSize}
    viewBox={`0 0 ${iconSize} ${iconSize}`}
    fill="none"
  >
    <path
      d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM9 12H7V7H9V12ZM8 6C7.4 6 7 5.6 7 5C7 4.4 7.4 4 8 4C8.6 4 9 4.4 9 5C9 5.6 8.6 6 8 6Z"
      fill={fill || theme.COLORS.BRAND_DEFAULT}
    />
  </svg>
)

const Content = styled(Flex)<IContentProps>`
  overflow: hidden;

  justify-content: center;

  width: max-content;
  margin: ${space.s};
  min-width: ${rem(100)};
  max-width: ${({ maxWidth }) => maxWidth || rem(250)};

  background-color: ${theme.COLORS.WHITE};
  border-radius: 8px;
  box-shadow: ${BOXSHADOWS.TOOLTIP};

  ${({ hasText }) => !hasText && `padding: ${space.s};`}
`

const StyledText = styled(Text)`
  width: 100%;
  padding: ${space.s};

  font-size: ${fontSizes.xs};
  line-height: 1.2;
  white-space: initial;
  text-align: ${({ textAlign }) => textAlign || 'center'};

  background-color: ${theme.COLORS.BG_DARK};
  color: ${theme.COLORS.WHITE};
`

const LabelText = styled(Text)`
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`

export const Tooltip = ({
  text,
  label,
  onClick,
  maxWidth,
  position,
  children,
  iconFill,
  iconSize,
  textAlign,
  contentComp,
  fontSize = 'xxs',
  width = 'fit-content',
  justifyContent,
}: ITooltipProps) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const onHover = debounce((value) => setIsTooltipVisible(value))

  return (
    <Popover
      placement="right"
      isVisible={isTooltipVisible}
      targetComp={
        <TooltipRef
          width={width}
          alignItems="center"
          justifyContent={justifyContent}
          onClick={onClick}
          onMouseEnter={() => {
            onHover(true)
          }}
          onMouseLeave={() => {
            onHover(false)
          }}
        >
          {label && (
            <LabelText fontSize={fontSize} color={iconFill} mr="s">
              {label}
            </LabelText>
          )}
          {children || <Info fill={iconFill} iconSize={iconSize} />}
        </TooltipRef>
      }
      zIndex={10}
    >
      {isTooltipVisible && (
        <Content
          onMouseEnter={() => {
            onHover(true)
          }}
          onMouseLeave={() => {
            onHover(false)
          }}
          hasText={!!text}
          //@ts-ignore
          position={position}
          maxWidth={maxWidth}
        >
          {contentComp || <StyledText textAlign={textAlign}>{text}</StyledText>}
        </Content>
      )}
    </Popover>
  )
}
